/**
 * Tiny global registry of "there is unsaved work on screen right now".
 *
 * Lets app-level chrome make safer decisions — chiefly: don't force a
 * service-worker (deploy) reload while someone is mid-edit. Components register
 * a stable key and flip it on/off as their dirty state changes; they MUST clear
 * it on unmount. Purely advisory — losing this signal never loses data (the
 * timesheet form also autosaves locally), it just makes reloads politer.
 */

const dirtyKeys = new Set<string>();
const listeners = new Set<() => void>();

/** Mark a keyed piece of work dirty (true) or clean/gone (false). */
export function setUnsavedWork(key: string, isDirty: boolean): void {
  if (!key) return;
  const had = dirtyKeys.has(key);
  if (isDirty) dirtyKeys.add(key);
  else dirtyKeys.delete(key);
  if (had !== dirtyKeys.has(key)) {
    for (const l of listeners) {
      try { l(); } catch { /* a listener must never break the registry */ }
    }
  }
}

/** True when any registered work is currently unsaved. */
export function hasUnsavedWork(): boolean {
  return dirtyKeys.size > 0;
}

/** Subscribe to dirty/clean transitions. Returns an unsubscribe fn. */
export function onUnsavedWorkChange(cb: () => void): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}
